/* /assets/section-product-sticky-atc.js */

if (!customElements.get('product-sticky-atc')) {
  customElements.define(
    'product-sticky-atc',
    class ProductStickyAtc extends HTMLElement {
      constructor() {
        super();
        this._mainAtcObserver = null;
        this._hideNearObserver = null;
        this._cartDrawerObserver = null;
        this._priceObserver = null;
        this._kachingObserver = null;
        this._kachingAttachObserver = null;
        this._kachingAbort = null;
        this._quantityAbort = null;
        this._atcClickAbort = null;
        this._processingAbort = null;
        this._processingObserver = null;
        this._mainAtcVisible = true;
        this._hideNearVisible = false;
        this._cartOpen = false;
        this._visibleHideNearEls = new Set();
      }

      connectedCallback() {
        this._observeMainAtc();
        this._observeHideNearSections();
        this._observeCartDrawer();
        this._bindAtcClick();
        this._bindProcessingSync();
        this._bindQuantitySync();
        this._observePriceSync();
        this._bindKachingBundleSync();
        this._syncPrice();
        this._syncDisabledState();
      }

      disconnectedCallback() {
        this._mainAtcObserver?.disconnect();
        this._hideNearObserver?.disconnect();
        this._cartDrawerObserver?.disconnect();
        this._priceObserver?.disconnect();
        this._kachingObserver?.disconnect();
        this._kachingAttachObserver?.disconnect();
        this._kachingAbort?.abort();
        this._quantityAbort?.abort();
        this._atcClickAbort?.abort();
        this._processingAbort?.abort();
        this._processingObserver?.disconnect();
        this._mainAtcObserver = null;
        this._hideNearObserver = null;
        this._cartDrawerObserver = null;
        this._priceObserver = null;
        this._kachingObserver = null;
        this._kachingAttachObserver = null;
        this._kachingAbort = null;
        this._quantityAbort = null;
        this._atcClickAbort = null;
        this._processingAbort = null;
        this._processingObserver = null;
      }

      _getProductInfo() {
        return document.querySelector('product-info');
      }

      _getMainQuantityInput() {
        const productInfo = this._getProductInfo();
        const sectionId = productInfo?.dataset?.section;
        if (!sectionId) {
          return null;
        }

        const input = productInfo.querySelector(`#Quantity-${sectionId}`);
        return input instanceof HTMLInputElement ? input : null;
      }

      /**
       * Sticky qty stepper only when the PDP quantity UI exists and is visible.
       * On product-T1 / Kaching HPP the native qty is often display:none — hide stepper.
       * @param {HTMLInputElement | null} input
       * @returns {boolean}
       */
      _isMainQuantityVisible(input) {
        if (!(input instanceof HTMLInputElement)) {
          return false;
        }

        const wrap = input.closest('.product__quantity') || input.closest('quantity-selector') || input;
        const style = window.getComputedStyle(wrap);
        if (style.display === 'none' || style.visibility === 'hidden') {
          return false;
        }

        const rect = wrap.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) {
          return false;
        }

        return true;
      }

      _parseQuantity(input) {
        let q = Number.parseInt(input.value, 10);
        if (!Number.isFinite(q) || q < 1) {
          q = 1;
        }

        const min = Number.parseInt(input.getAttribute('min') || '1', 10);
        const maxAttr = input.getAttribute('max');
        const max = maxAttr === null ? Number.POSITIVE_INFINITY : Number.parseInt(maxAttr, 10);

        if (Number.isFinite(min) && q < min) {
          q = min;
        }
        if (Number.isFinite(max) && q > max) {
          q = max;
        }

        return q;
      }

      _syncQtyDisplay() {
        const input = this._getMainQuantityInput();
        const qtyWrap = this.querySelector('[data-product-sticky-atc-quantity]');
        const display = this.querySelector('[data-product-sticky-atc-qty-value]');

        if (!display) {
          return;
        }

        if (!input || !this._isMainQuantityVisible(input)) {
          qtyWrap?.setAttribute('hidden', '');
          return;
        }

        qtyWrap?.removeAttribute('hidden');
        display.textContent = String(this._parseQuantity(input));
        this._syncStepperDisabledState();
      }

      _syncStepperDisabledState() {
        const input = this._getMainQuantityInput();
        const minus = this.querySelector('[data-product-sticky-atc-qty-minus]');
        const plus = this.querySelector('[data-product-sticky-atc-qty-plus]');

        if (
          !input ||
          !this._isMainQuantityVisible(input) ||
          !(minus instanceof HTMLButtonElement) ||
          !(plus instanceof HTMLButtonElement)
        ) {
          return;
        }

        const q = this._parseQuantity(input);
        const min = Number.parseInt(input.getAttribute('min') || '1', 10);
        const maxAttr = input.getAttribute('max');
        const max = maxAttr === null ? Number.POSITIVE_INFINITY : Number.parseInt(maxAttr, 10);

        minus.disabled = (q <= min);
        plus.disabled = (Number.isFinite(max) && (q >= max));
      }

      _writeMainQuantity(newValue) {
        const input = this._getMainQuantityInput();
        if (!input) {
          return;
        }

        const min = Number.parseInt(input.getAttribute('min') || '1', 10);
        const maxAttr = input.getAttribute('max');
        const max = maxAttr === null ? Number.POSITIVE_INFINITY : Number.parseInt(maxAttr, 10);

        let q = newValue;
        if (!Number.isFinite(q) || q < 1) {
          q = 1;
        }
        if (Number.isFinite(min) && q < min) {
          q = min;
        }
        if (Number.isFinite(max) && q > max) {
          q = max;
        }

        input.value = String(q);
        input.dispatchEvent(new Event('input', {bubbles: true}));
        input.dispatchEvent(new Event('change', {bubbles: true}));
      }

      _adjustMainQuantity(delta) {
        const input = this._getMainQuantityInput();
        if (!input) {
          return;
        }

        this._writeMainQuantity(this._parseQuantity(input) + delta);
      }

      _bindQuantitySync() {
        this._quantityAbort?.abort();
        this._quantityAbort = new AbortController();
        const {signal} = this._quantityAbort;

        const input = this._getMainQuantityInput();
        const qtyWrap = this.querySelector('[data-product-sticky-atc-quantity]');

        if (!input || !this._isMainQuantityVisible(input)) {
          qtyWrap?.setAttribute('hidden', '');
          return;
        }

        qtyWrap?.removeAttribute('hidden');

        const onMainQtyChange = () => {
          this._syncQtyDisplay();
        };

        input.addEventListener('input', onMainQtyChange, {signal});
        input.addEventListener('change', onMainQtyChange, {signal});

        const productInfo = this._getProductInfo();
        const mainQtySelector = productInfo?.querySelector('quantity-selector');
        if (mainQtySelector) {
          mainQtySelector.addEventListener(
            'click',
            (event) => {
              const btn = event.target?.closest?.('button[name="plus"], button[name="minus"]');
              if (!btn) {
                return;
              }

              requestAnimationFrame(() => {
                this._syncQtyDisplay();
              });
            },
            {signal},
          );
        }

        const minus = this.querySelector('[data-product-sticky-atc-qty-minus]');
        const plus = this.querySelector('[data-product-sticky-atc-qty-plus]');

        if (minus instanceof HTMLButtonElement) {
          minus.addEventListener('click', () => {
            this._adjustMainQuantity(-1);
          }, {signal});
        }

        if (plus instanceof HTMLButtonElement) {
          plus.addEventListener('click', () => {
            this._adjustMainQuantity(1);
          }, {signal});
        }

        this._syncQtyDisplay();
      }

      _observeMainAtc() {
        const selector = this.dataset.mainAtcSelector || '.product__quantity_buttons-wrapper';
        const mainAtc = document.querySelector(selector);

        if (!mainAtc) {
          this._mainAtcVisible = false;
          this._updateVisibility();
          return;
        }

        this._mainAtcObserver = new IntersectionObserver(
          ([entry]) => {
            this._mainAtcVisible = entry.isIntersecting;
            this._updateVisibility();
          },
          {threshold: 0},
        );

        this._mainAtcObserver.observe(mainAtc);
      }

      _observeHideNearSections() {
        const raw = this.dataset.hideNearSelector?.trim() || '';
        if (!raw) {
          this._hideNearVisible = false;
          return;
        }

        const selectors = raw
          .split(',')
          .map((sel) => sel.trim())
          .filter(Boolean);

        const elements = selectors.flatMap((sel) => Array.from(document.querySelectorAll(sel)));

        if (elements.length === 0) {
          this._hideNearVisible = false;
          return;
        }

        this._hideNearObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                this._visibleHideNearEls.add(entry.target);
              } else {
                this._visibleHideNearEls.delete(entry.target);
              }
            }
            this._hideNearVisible = (this._visibleHideNearEls.size > 0);
            this._updateVisibility();
          },
          {threshold: 0},
        );

        for (const el of elements) {
          this._hideNearObserver.observe(el);
        }
      }

      _observeCartDrawer() {
        const drawer = document.getElementById('cart-drawer');
        if (!drawer) {
          return;
        }

        const syncCartOpen = () => {
          this._cartOpen = drawer.classList.contains('cart-open');
          this._updateVisibility();
        };

        this._cartDrawerObserver = new MutationObserver(syncCartOpen);
        this._cartDrawerObserver.observe(drawer, {
          attributes: true,
          attributeFilter: ['class'],
        });
        syncCartOpen();
      }

      _updateVisibility() {
        const shouldShow = !this._mainAtcVisible && !this._hideNearVisible && !this._cartOpen;
        this.classList.toggle('is-visible', shouldShow);
        this.setAttribute('aria-hidden', String(!shouldShow));

        if (shouldShow) {
          this._syncQtyDisplay();
        }
      }

      _bindAtcClick() {
        const btn = this.querySelector('[data-product-sticky-atc-submit]');
        if (!btn) {
          return;
        }

        this._atcClickAbort?.abort();
        this._atcClickAbort = new AbortController();
        const {signal} = this._atcClickAbort;

        btn.addEventListener(
          'click',
          () => {
            if (btn.hasAttribute('processing')) {
              return;
            }

            const mainForm = document.querySelector('product-info ajax-cart-product-form');
            const mainBtn = document.querySelector('product-info button[name="add"]');
            if (!mainForm || !(mainBtn instanceof HTMLButtonElement) || mainBtn.disabled) {
              return;
            }

            this._setStickyProcessing(true);
            mainBtn.click();
          },
          {signal},
        );
      }

      _bindProcessingSync() {
        this._processingAbort?.abort();
        this._processingAbort = new AbortController();
        const {signal} = this._processingAbort;

        const onRequestEnd = (event) => {
          if (event.detail?.requestState?.requestType !== 'add') {
            return;
          }

          this._setStickyProcessing(false);
        };

        document.addEventListener('liquid-ajax-cart:request-end', onRequestEnd, {signal});

        const mainForm = document.querySelector('product-info ajax-cart-product-form');
        if (!mainForm) {
          return;
        }

        this._processingObserver?.disconnect();
        this._processingObserver = new MutationObserver(() => {
          this._setStickyProcessing(mainForm.hasAttribute('processing'));
        });
        this._processingObserver.observe(mainForm, {
          attributes: true,
          attributeFilter: ['processing'],
        });
      }

      _setStickyProcessing(isProcessing) {
        const btn = this.querySelector('[data-product-sticky-atc-submit]');
        if (!(btn instanceof HTMLButtonElement)) {
          return;
        }

        if (isProcessing) {
          btn.setAttribute('processing', '');
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
          return;
        }

        btn.removeAttribute('processing');
        btn.removeAttribute('aria-busy');
        this._syncDisabledState();
      }

      _observePriceSync() {
        const productInfo = this._getProductInfo();
        if (!productInfo) {
          return;
        }

        this._priceObserver = new MutationObserver(() => {
          this._syncPrice();
          this._syncDisabledState();
        });

        this._priceObserver.observe(productInfo, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      /**
       * Sync selected Kaching deal-bar title + price into the sticky (HPP / product-T1).
       * Falls back to default price sync when no deal bar is present (Annual Pack).
       */
      _bindKachingBundleSync() {
        this._kachingAbort?.abort();
        this._kachingAbort = new AbortController();
        const {signal} = this._kachingAbort;

        const onKachingChange = () => {
          this._syncKachingSelection();
          this._syncQtyDisplay();
        };

        document.addEventListener('deal-bar-selected', onKachingChange, {signal});
        document.addEventListener('variants-changed', onKachingChange, {signal});

        this._kachingAttachObserver?.disconnect();
        this._kachingAttachObserver = new MutationObserver(() => {
          this._attachKachingObserver(onKachingChange);
        });
        this._kachingAttachObserver.observe(document.body, {childList: true, subtree: true});
        this._attachKachingObserver(onKachingChange);
        onKachingChange();
      }

      /**
       * @param {() => void} onKachingChange
       */
      _attachKachingObserver(onKachingChange) {
        const kaching = document.querySelector('.kaching-bundles');
        if (!kaching || this._kachingObserver) {
          return;
        }

        this._kachingObserver = new MutationObserver(onKachingChange);
        this._kachingObserver.observe(kaching, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class'],
        });
        onKachingChange();
      }

      _syncKachingSelection() {
        const selectedBar = document.querySelector('.kaching-bundles__bar--selected');
        const selectionTarget = this.querySelector('[data-product-sticky-atc-selection]');
        const priceTarget = this.querySelector('[data-product-sticky-atc-price]');
        const compareTarget = this.querySelector('[data-product-sticky-atc-compare]');

        if (!selectedBar) {
          if (selectionTarget) {
            selectionTarget.textContent = '';
            selectionTarget.hidden = true;
          }
          return false;
        }

        const titleEl = selectedBar.querySelector('.kaching-bundles__bar-title');
        const priceEl = selectedBar.querySelector('.kaching-bundles__bar-price');
        const title = titleEl?.textContent?.trim() || '';
        const price = priceEl?.textContent?.trim() || '';

        if (selectionTarget) {
          if (title) {
            // Prefer deal-bar title over product-level title for the selection line
            selectionTarget.textContent = title;
            selectionTarget.hidden = false;
          } else {
            selectionTarget.textContent = '';
            selectionTarget.hidden = true;
          }
        }

        if (price && priceTarget) {
          priceTarget.textContent = price;
        }

        if (compareTarget) {
          compareTarget.hidden = true;
        }

        return Boolean(title || price);
      }

      _syncPrice() {
        if (this._syncKachingSelection()) {
          return;
        }

        const source = document.querySelector('[data-individual-atc-total]');
        const target = this.querySelector('[data-product-sticky-atc-price]');
        if (source && target) {
          target.textContent = source.textContent?.trim() || target.textContent;
        }

        const compareSource = document.querySelector('.product__price .price__sale s, .product__price .price__regular s');
        const compareTarget = this.querySelector('[data-product-sticky-atc-compare]');
        if (compareSource && compareTarget) {
          compareTarget.textContent = compareSource.textContent?.trim() || '';
          compareTarget.hidden = !compareSource.textContent?.trim();
        } else if (compareTarget && !compareSource) {
          compareTarget.hidden = true;
        }
      }

      _syncDisabledState() {
        const stickyBtn = this.querySelector('[data-product-sticky-atc-submit]');
        const mainBtn = document.querySelector('product-info button[name="add"]');
        if (!(stickyBtn instanceof HTMLButtonElement) || !(mainBtn instanceof HTMLButtonElement)) {
          return;
        }

        if (stickyBtn.hasAttribute('processing')) {
          stickyBtn.disabled = true;
          return;
        }

        stickyBtn.disabled = mainBtn.disabled;
      }
    },
  );
}
